import dayjs from "dayjs";

/**
 * CLE-3015 — sharing a Clear Certified credential. The public verification page lives at
 * /certified/:credentialId (no login); the LinkedIn "Add to profile" link opens the
 * Licenses & Certifications form pre-filled with this certification and that page as its URL.
 */

/** ClearHealth's LinkedIn organization id (confirmed by the page admin). The only copy. */
export const LINKEDIN_ORGANIZATION_ID = "98759431";

export const CERTIFICATION_NAME = "Clear Certified";

export const LINKEDIN_ADD_TO_PROFILE_URL =
  "https://www.linkedin.com/profile/add";

export const certifiedPath = (credentialId: string) =>
  `/certified/${encodeURIComponent(credentialId)}`;

/** The public verification page for a credential, on this app's own origin. */
export const publicCredentialUrl = (origin: string, credentialId: string) =>
  `${origin.replace(/\/+$/, "")}${certifiedPath(credentialId)}`;

export interface ILinkedInCertification {
  credentialId: string;
  /** ISO timestamp of the certification; year and month are read in the viewer's local time, like the certificate's date. */
  certifiedAt: string;
  /** Usually `window.location.origin`. */
  origin: string;
}

/**
 * The pre-filled "Add to profile" link. No expiry fields: the certification does not expire.
 * `issueMonth` is 1-based (January = 1), as LinkedIn expects.
 */
export function buildLinkedInAddToProfileUrl({
  credentialId,
  certifiedAt,
  origin,
}: ILinkedInCertification): string {
  const issued = dayjs(certifiedAt);
  // encodeURIComponent rather than URLSearchParams: a space goes out as %20, never "+", which not
  // every reader of a pasted link decodes as a space.
  const params: [string, string][] = [
    ["startTask", "CERTIFICATION_NAME"],
    ["name", CERTIFICATION_NAME],
    ["organizationId", LINKEDIN_ORGANIZATION_ID],
    ["issueYear", String(issued.year())],
    ["issueMonth", String(issued.month() + 1)],
    ["certUrl", publicCredentialUrl(origin, credentialId)],
    ["certId", credentialId],
  ];
  const query = params
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return `${LINKEDIN_ADD_TO_PROFILE_URL}?${query}`;
}
